if (!customElements.get('treezyn-impizi-system')) {
  customElements.define('treezyn-impizi-system', class extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => this.initialize());
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.visibilityObserver?.disconnect();
      this.initialized = false;
    }

    initialize() {
      if (this.initialized || !this.isConnected) return;
      this.config = this.readJson(this.querySelector('[data-impizi-config]'));
      this.form = this.querySelector('[data-impizi-form]');
      if (!this.config || !this.form) return;
      this.initialized = true;
      this.labels = this.config.labels;
      this.sizeSelects = [...this.querySelectorAll('[data-bundle-option]')];
      this.button = this.querySelector('[data-add-button]');
      this.buttonLabel = this.querySelector('[data-add-label]');
      this.message = this.querySelector('[data-form-message]');
      this.cartLink = this.querySelector('[data-cart-link]');
      this.variantInput = this.querySelector('[data-bundle-id]');
      this.pending = false;
      this.addons = [...this.querySelectorAll('[data-impizi-addon]')].map((element) => ({
        element,
        data: this.readJson(element.querySelector('[data-addon-config]')),
        check: element.querySelector('[data-addon-check]'),
        select: element.querySelector('[data-addon-select]'),
        single: element.querySelector('[data-addon-single]'),
        controls: element.querySelector('[data-addon-controls]'),
        image: element.querySelector('[data-addon-image]'),
        price: element.querySelector('[data-addon-price]'),
        link: element.querySelector('[data-addon-link]')
      }));
      this.validBundle = this.config.optionNames?.length === 2
        && this.config.optionNames[0] === 'Parka Size'
        && this.config.optionNames[1] === 'Bibs Size'
        && Array.isArray(this.config.variants)
        && this.config.variants.length > 0;
      this.bundleAvailable = this.validBundle && this.config.variants.some((variant) => variant.available);
      this.sizeSelects.forEach((select) => { select.disabled = !this.bundleAvailable; });
      this.addons.forEach((addon) => { addon.check.disabled = !addon.data?.available || !this.bundleAvailable; });
      this.listeners = new AbortController();
      this.addEventListener('change', () => {
        if (!this.pending) {
          this.message.hidden = true;
          this.cartLink.hidden = true;
        }
        this.updateSelection();
      }, { signal: this.listeners.signal });
      this.form.addEventListener('submit', (event) => this.addSystem(event), { signal: this.listeners.signal });
      this.addEventListener('click', (event) => {
        const link = event.target.closest('[data-open-extras]');
        if (link && this.contains(link)) {
          const extras = this.querySelector('[data-impizi-extras]');
          if (extras) extras.open = true;
        }
      }, { signal: this.listeners.signal });
      this.updateSelection();
      this.initializeSticky();
    }

    readJson(element) {
      try { return JSON.parse(element?.textContent || ''); } catch { return null; }
    }

    money(cents) {
      const amount = Number(cents) / 100;
      try {
        return new Intl.NumberFormat(this.config.locale, {
          style: 'currency',
          currency: this.config.currency,
          minimumFractionDigits: Number(cents) % 100 === 0 ? 0 : 2,
          maximumFractionDigits: 2
        }).format(amount);
      } catch {
        return `${amount.toFixed(2)} ${this.config.currency}`;
      }
    }

    updateSelection() {
      const values = this.sizeSelects.map((select) => select.value);
      const complete = values.length === 2 && values.every(Boolean);
      this.selectedBundle = this.validBundle && complete
        ? this.config.variants.find((variant) => variant.options.every((value, index) => value === values[index]))
        : null;
      this.sizeSelects.forEach((select, index) => {
        [...select.options].forEach((option) => {
          if (!option.value) return;
          option.disabled = !this.config.variants.some((variant) => variant.available
            && variant.options[index] === option.value
            && variant.options.every((value, otherIndex) => otherIndex === index || !values[otherIndex] || value === values[otherIndex]));
        });
      });
      const price = this.selectedBundle?.price ?? this.config.price;
      const compare = this.selectedBundle?.comparePrice ?? this.config.comparePrice;
      this.querySelectorAll('[data-pair-price]').forEach((element) => {
        element.textContent = price > 0 ? this.money(price) : this.labels.unavailable;
      });
      this.querySelectorAll('[data-pair-compare]').forEach((element) => {
        element.hidden = compare <= price;
        element.textContent = this.money(compare);
      });
      this.querySelectorAll('[data-pair-saving]').forEach((element) => {
        element.hidden = compare <= price;
        element.textContent = this.labels.save.replace('__AMOUNT__', this.money(compare - price));
      });
      this.selectedAddons = [];
      let addonCount = 0;
      let incompleteAddon = false;
      let extrasPrice = 0;
      this.addons.forEach((addon) => {
        const selected = addon.check.checked;
        addon.controls.hidden = !selected;
        if (addon.select) {
          addon.select.disabled = !selected || this.pending;
          addon.select.required = selected;
        }
        const id = selected ? (addon.select?.value || addon.single?.value) : '';
        const variant = addon.data?.variants?.find((candidate) => String(candidate.id) === String(id));
        if (selected) {
          addonCount += 1;
          if (variant?.available) {
            extrasPrice += variant.price;
            this.selectedAddons.push({ id: variant.id, quantity: 1 });
          } else {
            incompleteAddon = true;
          }
        }
        addon.price.textContent = this.money(variant?.price ?? addon.data?.price ?? 0);
        const imageUrl = variant?.image || addon.data?.image;
        if (addon.image && imageUrl && addon.image.getAttribute('src') !== imageUrl) {
          addon.image.removeAttribute('srcset');
          addon.image.src = imageUrl;
        }
        if (addon.link && addon.data?.url) {
          const url = new URL(addon.data.url, window.location.origin);
          if (variant) url.searchParams.set('variant', variant.id);
          addon.link.href = `${url.pathname}${url.search}`;
        }
      });
      this.querySelector('[data-extras-summary]').hidden = addonCount === 0;
      this.querySelector('[data-extras-count]').textContent = `(${addonCount})`;
      this.querySelector('[data-extras-total]').textContent = incompleteAddon ? this.labels.pendingOptions : this.money(extrasPrice);
      this.querySelector('[data-system-total]').textContent = price > 0 ? this.money(price + extrasPrice) : this.labels.unavailable;
      this.variantInput.value = this.selectedBundle?.available ? this.selectedBundle.id : '';
      this.variantInput.disabled = !this.selectedBundle?.available;
      this.ready = Boolean(this.selectedBundle?.available) && !incompleteAddon;
      this.button.disabled = !this.ready || this.pending;
      this.button.setAttribute('aria-busy', String(this.pending));
      this.querySelector('[data-selection-status]').textContent = !this.bundleAvailable || (complete && !this.selectedBundle?.available)
        ? this.labels.unavailable
        : complete ? this.labels.ready : this.labels.chooseBoth;
      this.buttonLabel.textContent = this.pending ? this.labels.adding
        : !this.bundleAvailable || (complete && !this.selectedBundle?.available) ? this.labels.unavailable
        : !complete ? this.labels.chooseSizesButton
        : incompleteAddon ? this.labels.pendingOptions : this.labels.add;
    }

    setPending(value) {
      this.pending = value;
      this.form.setAttribute('aria-busy', String(value));
      this.sizeSelects.forEach((select) => { select.disabled = value || !this.bundleAvailable; });
      this.addons.forEach((addon) => { addon.check.disabled = value || !addon.data?.available || !this.bundleAvailable; });
      this.updateSelection();
    }

    showMessage(message, isError = false) {
      this.message.textContent = message;
      this.message.dataset.error = String(isError);
      this.message.hidden = false;
      this.cartLink.hidden = false;
      if (isError) this.message.focus({ preventScroll: true });
    }

    publishThemeEvent(name, data) {
      if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS[name]) return;
      try { Promise.resolve(publish(PUB_SUB_EVENTS[name], data)).catch(() => {}); } catch {}
    }

    createStandardEvent(items) {
      const CartLinesUpdateEvent = window.StandardEvents?.CartLinesUpdateEvent;
      if (!CartLinesUpdateEvent?.createPromise) return null;
      try {
        const deferred = CartLinesUpdateEvent.createPromise();
        deferred.promise.catch(() => {});
        this.dispatchEvent(new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
          promise: deferred.promise
        }));
        return deferred;
      } catch { return null; }
    }

    async resolveStandardEvent(deferred) {
      if (!deferred) return;
      try {
        const url = new URL(this.config.cartUrl, window.location.origin);
        url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
        const response = await fetch(url.toString(), { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(this.labels.unconfirmed);
        const cart = await response.json();
        const CartLinesUpdateEvent = window.StandardEvents?.CartLinesUpdateEvent;
        if (!cart?.currency || !CartLinesUpdateEvent?.createCartFromAjaxResponse) throw new Error(this.labels.unconfirmed);
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
      } catch (error) { deferred.reject(error); }
    }

    async addSystem(event) {
      event.preventDefault();
      if (this.pending) return;
      this.updateSelection();
      if (!this.ready) {
        const missing = this.sizeSelects.find((select) => !select.value)
          || this.addons.find((addon) => addon.check.checked && addon.select && !addon.select.value)?.select;
        if (missing) {
          const disclosure = missing.closest('details');
          if (disclosure) disclosure.open = true;
          this.querySelector('[data-impizi-extras]')?.setAttribute('open', '');
          missing.focus();
          missing.reportValidity();
        }
        return;
      }
      const variantId = this.selectedBundle.id;
      const items = [{ id: variantId, quantity: 1 }, ...this.selectedAddons];
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      let sections = [];
      if (typeof cart?.getSectionsToRender === 'function') {
        try { sections = cart.getSectionsToRender().map((section) => section.id).slice(0, 5); } catch {}
      }
      if (typeof cart?.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
      const body = { items };
      if (sections.length) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }
      this.message.hidden = true;
      this.cartLink.hidden = true;
      this.setPending(true);
      const deferred = this.createStandardEvent(items);
      const requestController = new AbortController();
      const requestTimeout = window.setTimeout(() => requestController.abort(), 20000);
      let confirmed = false;
      let responseReceived = false;
      try {
        const url = new URL(this.config.cartAddUrl, window.location.origin);
        if (!url.pathname.endsWith('.js')) url.pathname += '.js';
        const response = await fetch(url.toString(), {
          method: 'POST',
          signal: requestController.signal,
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body)
        });
        const payload = await response.json();
        responseReceived = true;
        if (!response.ok || (payload.status && Number(payload.status) >= 400)) {
          const error = new Error(typeof payload.description === 'string' ? payload.description : this.labels.error);
          error.cartRejected = true;
          throw error;
        }
        confirmed = true;
        this.resolveStandardEvent(deferred);
        this.publishThemeEvent('cartUpdate', { source: 'impizi-system', productVariantId: String(variantId), cartData: payload });
        this.showMessage(this.labels.added);
        const canRender = typeof cart?.renderContents === 'function' && sections.length
          && sections.every((id) => typeof payload.sections?.[id] === 'string' && payload.sections[id].trim());
        if (canRender) {
          try {
            const normalized = { ...payload, id: payload.id || payload.items?.[0]?.id, key: payload.key || payload.items?.[0]?.key };
            cart.classList.remove('is-empty');
            cart.renderContents(normalized);
          } catch {
            window.location.assign(this.config.cartUrl);
          }
        } else {
          window.location.assign(this.config.cartUrl);
        }
      } catch (error) {
        if (confirmed) {
          window.location.assign(this.config.cartUrl);
        } else {
          const message = responseReceived && error.cartRejected ? error.message : this.labels.unconfirmed;
          this.showMessage(message, true);
          this.publishThemeEvent('cartError', { source: 'impizi-system', productVariantId: String(variantId), errors: message, message });
          deferred?.reject(error);
        }
      } finally {
        window.clearTimeout(requestTimeout);
        if (this.isConnected) this.setPending(false);
      }
    }

    initializeSticky() {
      const sticky = this.querySelector('[data-impizi-sticky]');
      const hero = this.querySelector('[data-impizi-hero]');
      const summary = this.querySelector('[data-purchase-summary]');
      if (!sticky || !hero || !summary || !('IntersectionObserver' in window)) return;
      const update = () => {
        const heroBelow = hero.getBoundingClientRect().bottom > 0;
        const summaryRect = summary.getBoundingClientRect();
        const summaryVisible = summaryRect.top < window.innerHeight && summaryRect.bottom > 0;
        const sectionRect = this.getBoundingClientRect();
        sticky.hidden = heroBelow || summaryVisible || sectionRect.bottom <= 90 || sectionRect.top >= window.innerHeight;
      };
      this.visibilityObserver = new IntersectionObserver(update, { threshold: [0, 1] });
      [this, hero, summary].forEach((element) => this.visibilityObserver.observe(element));
      update();
    }
  });
}
